/**
 * The origins (scheme + host + port) of the two APIs the browser talks to
 * directly, read from the same env vars every other call site uses.
 *
 * Shared by `useClientApi` (which must only ever attach credentials to these
 * origins) and the RUM redaction (which scrubs query values on requests to
 * them), so the two can never disagree about what "our API" is.
 */

export type ApiBackend = "constellation" | "cosmic";

/**
 * `new URL(value).origin`, or `null` when the value is unset, not an absolute
 * URL, or has an opaque origin. Trailing slashes and paths on the configured
 * base (which `constellationEndpoint` strips and `mailtechApi` concatenates
 * onto) are irrelevant to the origin, so both normalizations agree here.
 */
export function originOf(value: string | undefined | null): string | null {
  if (!value) return null;
  try {
    const { origin } = new URL(value);
    return origin && origin !== "null" ? origin : null;
  } catch {
    return null;
  }
}

/** The configured origin for one backend, or `null` when misconfigured. */
export function apiOrigin(backend: ApiBackend): string | null {
  // Each env var is referenced literally so Next inlines it into the client
  // bundle; a computed `process.env[name]` would be `undefined` in the browser.
  return backend === "constellation"
    ? originOf(process.env.NEXT_PUBLIC_CONSTELLATION_API_URL)
    : originOf(process.env.NEXT_PUBLIC_MAILTECH_API);
}
